import { cloneElement, ComponentType, ReactElement } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';

import { Button } from '@com/_atoms/NewButton';
import { colors } from '@configs/Theme';
import { CarouselTexts } from '@constant/ocs/cards';
import Icon from '@utilities/icon';

const ScrollSlider = dynamic(() => import('@com/shared/ocs/scroll-slider'));

export enum showMoreTypeEnum {
  HALF = 'half',
  FULL = 'full',
  NAKED = 'naked',
}
type CarouselItem = {
  id: number;
};

type CarouselItemProps<T> = {
  item: T;
  hasLabel?: boolean;
  onClick?: () => void;
};

interface CarouselProps<T extends CarouselItem> {
  title?: string;
  items: T[];
  labeledItemIds?: number[];
  onItemClick?: (item: T) => void;
  onShowMore?: () => void;
  showMoreLabel?: string;
  showMoreClassName?: string;
  showMoreType?: showMoreTypeEnum;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  headerClassName?: string;
  containerClassName?: string;
  scrollSliderClassName?: string;
  ItemComponent: ComponentType<CarouselItemProps<T>>;
  ShimmerComponent?: ReactElement;
  isLoading?: boolean;
  shimmerCount?: number;
  showMoreContainerClassName?: string;
}

const Carousel = <T extends CarouselItem>({
  title,
  items,
  labeledItemIds = [],
  onItemClick,
  onShowMore,
  showMoreLabel = CarouselTexts?.viewAll,
  showMoreClassName,
  showMoreType = showMoreTypeEnum.HALF,
  backgroundColor,
  headerBackgroundColor,
  headerClassName,
  containerClassName,
  scrollSliderClassName,
  ItemComponent,
  ShimmerComponent,
  isLoading = false,
  shimmerCount = 6,
  showMoreContainerClassName,
}: CarouselProps<T>) => {
  const isShowMoreButton = !!onShowMore;

  const showMoreContainer = {
    [showMoreTypeEnum.HALF]: 'justify-start h-full',
    [showMoreTypeEnum.FULL]: 'bg-surface-secondary rounded-lg justify-center',
    [showMoreTypeEnum.NAKED]: 'justify-center',
  };
  return (
    <div
      className={classNames('h-[164px] flex flex-col', containerClassName)}
      style={backgroundColor ? { backgroundColor } : {}}
    >
      <div
        className={classNames(
          'h-[56px] flex items-center justify-between px-4',
          headerClassName,
        )}
        style={
          headerBackgroundColor
            ? { backgroundColor: headerBackgroundColor }
            : {}
        }
      >
        <div className="flex items-center">
          <span
            className={classNames('text-Label/Medium text-content-primary')}
          >
            {title}
          </span>
        </div>

        {isShowMoreButton && (
          <Button
            variant="secondary"
            size="medium"
            className="h-10 w-10 rounded-full !p-0"
            onClick={onShowMore}
            icon={
              <Icon
                name="ChevronLeft"
                width={1.5}
                height={1.5}
                fill={colors.black}
              />
            }
          />
        )}
      </div>

      <ScrollSlider
        className={classNames(
          !isShowMoreButton && 'pl-4',
          'flex h-[108px] items-center gap-x-2 px-4 overflow-hidden',
          scrollSliderClassName,
        )}
      >
        <>
          {isLoading
            ? Array.from({ length: shimmerCount }).map((_, index) =>
                cloneElement(ShimmerComponent, { key: index }),
              )
            : items?.map((item) => (
                <ItemComponent
                  key={item?.id}
                  item={item}
                  hasLabel={labeledItemIds?.includes(item?.id)}
                  onClick={onItemClick ? () => onItemClick(item) : undefined}
                />
              ))}
        </>

        {isShowMoreButton && !isLoading && (
          <div
            className={classNames(
              'flex flex-col items-start',
              showMoreContainer[showMoreType],
              showMoreContainerClassName,
            )}
          >
            <div
              onClick={onShowMore}
              className={classNames(
                'min-w-[70px] flex flex-col items-center justify-center cursor-pointer bg-surface-secondary  rounded-lg',
                showMoreType === showMoreTypeEnum.NAKED &&
                  '!bg-surface-primary',
                showMoreClassName,
              )}
              onKeyDown={() => {
                return;
              }}
              tabIndex={0}
              role="button"
            >
              <Icon
                name="ArrowLeft"
                width={1.5}
                height={1.5}
                fill={colors?.black}
              />
            </div>
            <span className="text-content-primary text-Label/xSmall text-center w-full">
              {showMoreLabel}
            </span>
          </div>
        )}
      </ScrollSlider>
    </div>
  );
};

export default Carousel;
